'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const navLinks = [
    { id: 'nav_home', label: 'Home', href: '/homepage' },
    { id: 'nav_about', label: 'About', href: '/about' },
    { id: 'nav_courses', label: 'Courses', href: '/courses' },
    { id: 'nav_ai_marketing', label: 'AI Marketing', href: '/ai-digital-marketing' },
    { id: 'nav_landing', label: 'Special Offer', href: '/landing' },
    { id: 'nav_contact', label: 'Contact', href: '/contact' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-gradient-to-r from-teal-50 via-cyan-50 to-teal-50 shadow-lg py-4'
            : 'bg-transparent py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3">
            <AppImage
              src="/assets/images/apollologo_website-1770289244161.png"
              alt="Apollobits Academy logo with graduation cap and book icon"
              width={180}
              height={50}
              className="h-12 w-auto"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks?.map((link) => (
              <Link
                key={link?.id}
                href={link?.href}
                className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                  pathname === link?.href
                    ? 'text-primary'
                    : isScrolled
                    ? 'text-foreground hover:text-primary'
                    : 'text-white hover:text-primary'
                }`}
              >
                {link?.label}
              </Link>
            ))}
            <Link
              href="/contact"
              className="bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors"
            >
              Enroll Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="md:hidden p-2 -mr-2"
            aria-label="Toggle Menu"
          >
            <Icon
              name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={28}
              className={isScrolled ? 'text-foreground' : 'text-white'}
            />
          </button>
        </div>
      </nav>
      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-secondary z-40 transform transition-transform duration-500 ${
          isMenuOpen ? 'translate-y-0' : 'translate-y-full'
        } md:hidden`}
      >
        <div className="flex flex-col justify-center items-center h-full gap-8 px-6">
          {navLinks?.map((link) => (
            <Link
              key={link?.id}
              href={link?.href}
              onClick={toggleMenu}
              className={`font-display text-5xl uppercase tracking-tighter transition-colors ${
                pathname === link?.href
                  ? 'text-primary' :'text-white hover:text-primary'
              }`}
            >
              {link?.label}
            </Link>
          ))}
          <Link
            href="/contact"
            onClick={toggleMenu}
            className="bg-primary text-primary-foreground px-8 py-4 rounded-full text-base font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors mt-4"
          >
            Enroll Now
          </Link>
        </div>
      </div>
    </>
  );
}